import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import {
  createMissionFeeQuery,
  editMissionFeeQuery,
  getMissionFeeByIdQuery,
  getMissionFeeByMissionIdQuery,
  deleteMissionFeeQuery,
  getCurrentMissionFeeQuery,
  getMissionFeeByMissionIdArrayQuery,
} from "./queries";
import { useUserContext } from "api/user/UserProvider";
import { QUERY_STATUS, ROLE_MATCH_MODE, SCOPES } from "constants/constants";
import { canUserPerformAction } from "access-roles/general-functions";
import { MISSION_FEE, MISSION_ROLES } from "access-roles/roles";

const MissionFeeContext = createContext({});

export const useMissionFeeContext = () => useContext(MissionFeeContext);

export const MissionFeeProvider = ({ ...props }) => {
  const [missionFeeByMissionId, setMissionFeeByMissionId] = useState([]);
  const [missionFeeById, setMissionFeeById] = useState([]);
  const [currentMissionFee, setCurrentMissionFee] = useState();
  const [missionFeeByMissionIdArray, setMissionFeeByMissionIdArray] = useState(
    []
  );
  const { userScope, userRoleList } = useUserContext();
  const [missionFeeEditStatus, setMissionFeeEditStatus] = useState(
    QUERY_STATUS.INITIAL
  );

  const getCurrentMissionFee = useCallback(
    async (id) => {
      if (
        !canUserPerformAction({
          userRoles: userRoleList,
          requiredRoles: MISSION_FEE.ROLE_GET_MISSION_FEE,
        })
      ) {
        return;
      }
      try {
        setCurrentMissionFee(
          await getCurrentMissionFeeQuery({ id, userScope })
        );
      } catch (error) {
        console.error(error);
      }
    },
    [userScope, userRoleList]
  );

  const createMissionFee = useCallback(
    async (data) => {
      try {
        setMissionFeeEditStatus(QUERY_STATUS.LOADING);
        await createMissionFeeQuery({ data, userScope });
        setMissionFeeEditStatus(QUERY_STATUS.SUCCESS);
      } catch (error) {
        setMissionFeeEditStatus(QUERY_STATUS.ERROR);
        console.error(error);
      }
    },
    [userScope]
  );

  const getMissionFeeByMissionId = useCallback(
    async (id) => {
      try {
        setMissionFeeByMissionId(
          await getMissionFeeByMissionIdQuery({ id, userScope })
        );
      } catch (error) {
        console.error(error);
      }
    },
    [setMissionFeeByMissionId, userScope]
  );

  const getMissionFeeById = useCallback(
    async ({ id }) => {
      try {
        setMissionFeeById(await getMissionFeeByIdQuery({ id, userScope }));
      } catch (error) {
        console.error(error);
      }
    },
    [userScope]
  );

  const editMissionFee = useCallback(
    async ({ id, data }) => {
      try {
        setMissionFeeEditStatus(QUERY_STATUS.LOADING);
        await editMissionFeeQuery({ id, data, userScope });
        setMissionFeeEditStatus(QUERY_STATUS.SUCCESS);
      } catch (error) {
        setMissionFeeEditStatus(QUERY_STATUS.ERROR);
        console.error(error);
      }
    },
    [userScope]
  );

  const deleteMissionFee = useCallback(
    async (id) => {
      try {
        setMissionFeeEditStatus(QUERY_STATUS.LOADING);
        await deleteMissionFeeQuery({ id, userScope });
        setMissionFeeEditStatus(QUERY_STATUS.SUCCESS);
      } catch (error) {
        setMissionFeeEditStatus(QUERY_STATUS.ERROR);
        console.error(error);
      }
    },
    [userScope]
  );

  const getMissionFeeByMissionIdArray = useCallback(
    async (ids) => {
      if (
        !canUserPerformAction({
          userRoles: userRoleList,
          requiredRoles: MISSION_FEE.ROLE_GET_MISSION_FEE,
        })
      ) {
        return;
      }
      try {
        setMissionFeeByMissionIdArray(
          await getMissionFeeByMissionIdArrayQuery({ ids })
        );
      } catch (error) {
        console.error(error);
      }
    },
    [setMissionFeeByMissionIdArray, userRoleList]
  );

  const values = useMemo(
    () => ({
      getMissionFeeByMissionIdArray,
      missionFeeByMissionIdArray,
      createMissionFee,
      missionFeeByMissionId,
      getMissionFeeByMissionId,
      getMissionFeeById,
      missionFeeById,
      editMissionFee,
      deleteMissionFee,
      getCurrentMissionFee,
      currentMissionFee,
      setCurrentMissionFee,
      missionFeeEditStatus,
      setMissionFeeEditStatus,
    }),
    [
      missionFeeByMissionIdArray,
      createMissionFee,
      missionFeeByMissionId,
      getMissionFeeByMissionId,
      getMissionFeeById,
      missionFeeById,
      editMissionFee,
      deleteMissionFee,
      currentMissionFee,
      setCurrentMissionFee,
      getCurrentMissionFee,
      missionFeeEditStatus,
      setMissionFeeEditStatus,
    ]
  );
  return <MissionFeeContext.Provider value={values} {...props} />;
};
